import { Color, colorToGraphicsOpts, DefaultLayerColorProvider } from '@cadstrom/renderer';
import { SchLayerType as SchLayerTypeEnum } from '@cadstrom/sigma-engine-shared';

import DefaultColorScheme from './colors/default-color-scheme.json';
import GrayscaleColorScheme from './colors/grayscale-color-scheme.json';
import SparkfunColorScheme from './colors/sparkfun-color-scheme.json';

import type { GraphicRenderStateOpts } from '@cadstrom/renderer';
import type { Layer, PcbLayerType, PcbModel, SchematicModel, SchLayerType } from '@cadstrom/sigma-engine-shared';

export interface IColorSchemeSet {
	name: string;
	colorSchemes: {
		light: IColorScheme;
		dark: IColorScheme;
	};
}

export interface IColorWithStyle {
	color: number | string;
	fill: 'solid';
}

export type ColorDefinition = number | string | IColorWithStyle;

export interface IColorScheme {
	pcbLayers: {
		colorsByName?: {
			[name: string]: ColorDefinition;
		};
		colorsById?: {
			[id: string]: ColorDefinition;
		};
		colorsByType: {
			layerType: PcbLayerType;
			front?: [ColorDefinition, ...ColorDefinition[]];
			back?: [ColorDefinition, ...ColorDefinition[]];
			colors: [ColorDefinition, ...ColorDefinition[]];
		}[];
	};
	schLayers: {
		colorsByName?: {
			[name: string]: ColorDefinition;
		};
		colorsById?: {
			[id: string]: ColorDefinition;
		};
		colorsByType: {
			layerType: SchLayerType;
			colors: [ColorDefinition, ...ColorDefinition[]];
		}[];
	};
	tools: {
		default: {
			default: ColorDefinition;
			overlay: ColorDefinition;
			selectedElements: ColorDefinition;
			userSelection: ColorDefinition;
			background: ColorDefinition;
		};
		iruler: {
			default: ColorDefinition;
			textShadow: ColorDefinition;
		};
		grid: {
			default: ColorDefinition;
			major?: ColorDefinition;
		};
		probe: {
			neg: ColorDefinition;
			pos: ColorDefinition;
		};
	};
	/** Tool colours that apply to schematic drawings only, taking precedence over `tools`. */
	schematicTools?: {
		canvas: {
			background: ColorDefinition;
		};
		grid: {
			default: ColorDefinition;
			major?: ColorDefinition;
		};
	};
}

export const ColorSchemes = {
	Default: DefaultColorScheme as any as IColorSchemeSet,
	Sparkfun: SparkfunColorScheme as any as IColorSchemeSet,
	Grayscale: GrayscaleColorScheme as any as IColorSchemeSet,
};

export class CadstromColorProvider extends DefaultLayerColorProvider {
	public activeScheme: IColorScheme;
	public get providerLayers() {
		return this.layers;
	}

	constructor(
		private colors: IColorSchemeSet,
		private themeMode: 'light' | 'dark',
		private drawingType: PcbModel['modelType'] | SchematicModel['modelType'],
		layers: Layer[],
		enableChangeWatchers = true,
	) {
		super(layers, enableChangeWatchers);
		this.activeScheme = this.colors.colorSchemes[this.themeMode];
		this.assignColors();
	}

	protected assignColors() {
		const unassignedLayers = Array.from(this.layers.values());
		const scheme = this.activeScheme;
		const layers = this.drawingType === 'pcb' ? scheme.pcbLayers : scheme.schLayers;
		if (layers.colorsById) {
			for (const layer of this.layers.values()) {
				const colorDef = layers.colorsById[layer.id.toString()];
				if (colorDef === undefined) {
					continue;
				}

				this.cache.set(layer.id, this.colorDefinitionToOpts(colorDef));
				unassignedLayers.splice(unassignedLayers.indexOf(layer), 1);
			}
		}

		if (layers.colorsByName) {
			for (const layer of this.layers.values()) {
				const colorDef = layers.colorsByName[layer.name];
				if (colorDef === undefined) {
					continue;
				}

				this.cache.set(layer.id, this.colorDefinitionToOpts(colorDef));
				unassignedLayers.splice(unassignedLayers.indexOf(layer), 1);
			}
		}

		for (const type of layers.colorsByType) {
			const typeLayers = unassignedLayers.filter((l) => l.type === type.layerType);
			const front: ColorDefinition[] = [...(('front' in type ? type.front : undefined) ?? [])];
			const back: ColorDefinition[] = [...(('back' in type ? type.back : undefined) ?? [])];
			const colors = [...type.colors];
			const usedColors = {
				generic: 0,
				F: 0,
				B: 0,
			};

			for (const layer of typeLayers) {
				let color = colors[usedColors.generic % colors.length]!;
				let used: keyof typeof usedColors = 'generic';
				if (layer.face === 'F' && front.length > 0) {
					color = front[usedColors[layer.face] % front.length]!;
					used = layer.face;
				} else if (layer.face === 'B' && back.length > 0) {
					color = back[usedColors[layer.face] % back.length]!;
					used = layer.face;
				}

				usedColors[used]++;
				this.cache.set(layer.id, this.colorDefinitionToOpts(color));
				unassignedLayers.splice(unassignedLayers.indexOf(layer), 1);
			}
		}
	}

	private colorDefinitionToOpts(colorDef: ColorDefinition): GraphicRenderStateOpts {
		switch (typeof colorDef) {
			case 'number': {
				return this.maybeAddChangeWatcher(colorToGraphicsOpts(colorDef, undefined, this.themeMode === 'dark'));
			}

			case 'string': {
				const color = Color.default(colorDef);
				let alpha: number | undefined = undefined;
				if (colorDef.includes('rgba')) {
					alpha = color.alpha();
				}

				return this.maybeAddChangeWatcher(colorToGraphicsOpts(color.rgbNumber(), alpha, this.themeMode === 'dark'));
			}

			default: {
				return this.maybeAddChangeWatcher(this.colorDefinitionToOpts(colorDef.color));
			}
		}
	}

	public override getColorForTool(tool: string, opt: string): GraphicRenderStateOpts {
		const key = this.generateToolCacheKey(tool, opt);
		if (this.cache.has(key)) {
			return this.cache.get(key)!;
		}

		const opts = this.maybeAddChangeWatcher(this.colorDefinitionToOpts(this.getToolColor(tool, opt)));
		this.cache.set(key, opts);
		return opts;
	}

	/**
	 * Returns the colour to paint behind the drawing, when the scheme sets one for this drawing type.
	 *
	 * @returns A CSS colour, or `undefined` to keep the app's own canvas colour.
	 */
	public getCanvasBackground(): string | undefined {
		const background =
			this.drawingType === 'schematic' ? this.activeScheme.schematicTools?.canvas.background : undefined;
		return background === undefined ? undefined : CadstromColorProvider.toCssColor(background);
	}

	/**
	 * Converts a colour definition to a CSS colour string.
	 *
	 * @param color - A colour as a CSS string or a 24-bit number, alone or with a style.
	 * @returns The CSS string as given, or the number as a `#rrggbb` hex colour.
	 */
	private static toCssColor(color: ColorDefinition): string {
		const value = typeof color === 'object' ? color.color : color;
		return typeof value === 'number' ? `#${value.toString(16).padStart(6, '0')}` : value;
	}

	/**
	 * Resolves a tool colour, preferring schematic-only tool colours on schematics and falling back
	 * to the default tool and option.
	 *
	 * @param tool - The tool to look up.
	 * @param opt - The tool option, falling back to `default`.
	 * @returns The colour definition; throws when neither the option nor `default` exists.
	 */
	private getToolColor(tool: string, opt: string): ColorDefinition {
		const schematicTools =
			this.drawingType === 'schematic' ?
				(this.activeScheme.schematicTools as Record<string, Record<string, ColorDefinition>> | undefined)
			:	undefined;
		let toolDef: Record<string, ColorDefinition> | undefined =
			schematicTools?.[tool] ?? (this.activeScheme.tools as Record<string, Record<string, ColorDefinition>>)[tool];
		toolDef ??= this.activeScheme.tools.default;

		const color = toolDef[opt] ?? toolDef['default'];
		if (color === undefined) {
			throw new Error(`Did not find opt ${opt} for tool ${tool}`);
		}

		return color;
	}

	private static readonly IMAGE_LAYER_OPTS: GraphicRenderStateOpts = {
		selected: { tint: 0xffffff, alpha: 1 },
		hover: { tint: 0xffffff, alpha: 1 },
		unselected: { tint: 0xffffff, alpha: 1 },
		selectionNeutral: { tint: 0xffffff, alpha: 1 },
	};

	protected override computeOptsForLayer(id: number): GraphicRenderStateOpts {
		// Image layer always uses white tint across all states to preserve original colors
		const layer = this.providerLayers.get(id);
		if (layer?.type === SchLayerTypeEnum.Image) {
			this.cache.set(id, CadstromColorProvider.IMAGE_LAYER_OPTS);
			return CadstromColorProvider.IMAGE_LAYER_OPTS;
		}

		if (!this.cache.has(id)) {
			const scheme = this.activeScheme;
			const layers = this.drawingType === 'pcb' ? scheme.pcbLayers : scheme.schLayers;
			if (layers.colorsById && layers.colorsById[id.toString()] !== undefined) {
				this.cache.set(id, this.colorDefinitionToOpts(layers.colorsById[id.toString()]!));
				return this.cache.get(id)!;
			}
			const { tool, opt } = this.getToolForLayer(id);
			this.cache.set(id, this.colorDefinitionToOpts(this.getToolColor(tool, opt)));
		}

		return this.cache.get(id)!;
	}
}
